/**
 * Order type comes only from the saved orders.order_type value — never from
 * customer name, meal preference, payment method or amount.
 */
export type SavedOrderType = "a_la_carte" | "meal_plan" | "tiffin";

export function orderTypeLabel(type: unknown): string {
  switch (type) {
    case "a_la_carte":
      return "À La Carte";
    case "meal_plan":
      return "Meal Plan";
    case "tiffin":
      return "Tiffin";
    default:
      return "Needs review";
  }
}

export type TiffinSubscriptionSnapshot = {
  id: string;
  order_id: string;
  status: string;
  duration_label: string | null;
  meal_selection: string | null;
  service_days: number | null;
  schedule_snapshot: unknown;
  start_date: string | null;
  price_snapshot: unknown;
  address_snapshot: unknown;
};

/** Saved per-delivery dates for a Tiffin subscription (YYYY-MM-DD, IST calendar days). */
export function tiffinDates(sub: Pick<TiffinSubscriptionSnapshot, "schedule_snapshot">): string[] {
  return Array.isArray(sub.schedule_snapshot)
    ? sub.schedule_snapshot.filter((d): d is string => typeof d === "string" && /^\d{4}-\d{2}-\d{2}$/.test(d))
    : [];
}

/** Saved meal slots for a Tiffin subscription ("lunch", "dinner" or both). */
export function tiffinSlots(sub: Pick<TiffinSubscriptionSnapshot, "meal_selection">): string[] {
  const v = sub.meal_selection ?? "";
  if (v === "lunch" || v === "dinner") return [v];
  if (v === "both" || v === "lunch_dinner" || v === "lunch_and_dinner") return ["lunch", "dinner"];
  return [];
}

/** Item title built only from saved subscription fields. */
export function tiffinTitle(sub: Pick<TiffinSubscriptionSnapshot, "duration_label">): string {
  return sub.duration_label ? `Vegetarian Tiffin — ${sub.duration_label}` : "Vegetarian Tiffin";
}

/** Tiffin has no per-booking dietary field: the supported catalogue is vegetarian-only. */
export function orderDietaryLabel(type: unknown, mealType: unknown): string {
  if (type === "tiffin") return "Pure Veg";
  if (mealType === "veg") return "Veg";
  if (mealType === "non_veg") return "Non-veg";
  return "Not recorded";
}

export function tiffinBookingDetails(sub: Pick<TiffinSubscriptionSnapshot, "duration_label" | "service_days" | "meal_selection" | "schedule_snapshot" | "price_snapshot">) {
  const meals = tiffinSlots(sub);
  const price = sub.price_snapshot && typeof sub.price_snapshot === "object" && !Array.isArray(sub.price_snapshot)
    ? sub.price_snapshot as Record<string, unknown> : {};
  return {
    plan_title: tiffinTitle(sub),
    dietary_preference: orderDietaryLabel("tiffin", null),
    duration_label: sub.duration_label,
    service_days: sub.service_days,
    meals,
    delivery_dates: tiffinDates(sub),
    delivery_windows: meals.flatMap((slot) => {
      const window = price[`${slot}_window`];
      return typeof window === "string" && window.trim() ? [window] : [];
    }),
  };
}
